import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import { useAuditApprovals } from "services/hooks/auditApprovals";
import { useOrganisations } from "services/hooks/rentals";
import {
  AMOUNT_SENSITIVE_ENTITIES,
  POLICY_ROLES,
  POLICY_SCOPES,
  buildPolicyStepPayload,
} from "../constants";

const emptyStep = {
  order: 1,
  role: "manager",
  required_approvals: 1,
  scope: "organization",
  user_id: "",
};

const ApprovalPoliciesTab = () => {
  const role = useSelector((state) => state.role);
  const isAdmin = role === "admin";
  const {
    entities,
    policies,
    isLoadingPolicies,
    policiesError,
    policyActionError,
    isSubmittingPolicy,
    savePolicy,
    deactivatePolicy,
    refresh,
    fetchEligibleApprovers,
  } = useAuditApprovals();
  const { organisations } = useOrganisations({ enabled: isAdmin });

  const approvalEntities = (entities ?? []).filter((e) => e.approval_supported);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [eligibleApprovers, setEligibleApprovers] = useState([]);
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(false);
  const [form, setForm] = useState({
    organization_id: "",
    entity_code: "",
    action: "CREATE",
    name: "",
    enabled: true,
    min_amount: "",
    steps: [{ ...emptyStep }],
  });

  const selectedEntity = approvalEntities.find((e) => e.code === form.entity_code);
  const showMinAmount = AMOUNT_SENSITIVE_ENTITIES.includes(form.entity_code);
  const hasUserStep = form.steps.some((s) => s.role === "user");

  const resolveOrganizationId = useCallback(() => {
    if (isAdmin && form.organization_id) return Number(form.organization_id);
    if (editingPolicy?.organization_id) return editingPolicy.organization_id;
    return undefined;
  }, [isAdmin, form.organization_id, editingPolicy]);

  const loadEligibleApprovers = useCallback(async () => {
    if (!dialogOpen) return;
    if (isAdmin && !form.organization_id && !editingPolicy?.organization_id) {
      setEligibleApprovers([]);
      return;
    }
    setIsLoadingApprovers(true);
    const orgId = resolveOrganizationId();
    const result = await fetchEligibleApprovers(orgId);
    setEligibleApprovers(result.success ? result.data : []);
    setIsLoadingApprovers(false);
  }, [
    dialogOpen,
    isAdmin,
    form.organization_id,
    editingPolicy,
    resolveOrganizationId,
    fetchEligibleApprovers,
  ]);

  useEffect(() => {
    if (!dialogOpen || !hasUserStep) return;
    if (isAdmin && !form.organization_id && !editingPolicy?.organization_id) {
      setEligibleApprovers([]);
      return;
    }
    loadEligibleApprovers();
  }, [
    dialogOpen,
    hasUserStep,
    isAdmin,
    form.organization_id,
    editingPolicy,
    loadEligibleApprovers,
  ]);

  const openCreate = () => {
    setEditingPolicy(null);
    const first = approvalEntities[0];
    setForm({
      organization_id: "",
      entity_code: first?.code ?? "",
      action: first?.actions?.[0] ?? "CREATE",
      name: "",
      enabled: true,
      min_amount: "",
      steps: [{ ...emptyStep }],
    });
    setDialogOpen(true);
  };

  const openEdit = (policy) => {
    setEditingPolicy(policy);
    setForm({
      organization_id: policy.organization_id ?? "",
      entity_code: policy.entity_code,
      action: policy.action,
      name: policy.name ?? "",
      enabled: policy.enabled !== false,
      min_amount: policy.min_amount ?? "",
      steps: (policy.steps ?? [{ ...emptyStep }]).map((s, i) => ({
        order: s.order ?? i + 1,
        role: s.role ?? "manager",
        required_approvals: s.required_approvals ?? 1,
        scope: s.scope ?? "organization",
        user_id: s.user_id ?? "",
      })),
    });
    setDialogOpen(true);
  };

  const handleStepChange = (index, field, value) => {
    setForm((prev) => {
      const steps = [...prev.steps];
      const updated = { ...steps[index], [field]: value, order: index + 1 };
      if (field === "role") {
        if (value === "user") {
          updated.scope = "organization";
        } else {
          updated.user_id = "";
          if (!updated.scope) updated.scope = "organization";
        }
      }
      steps[index] = updated;
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setForm((prev) => ({
      ...prev,
      steps: [...prev.steps, { ...emptyStep, order: prev.steps.length + 1 }],
    }));
  };

  const removeStep = (index) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 })),
    }));
  };

  const handleSubmit = async () => {
    if (!form.entity_code || !form.action || !form.name.trim()) {
      setMessage({ type: "error", text: "Entity, action, and policy name are required." });
      return;
    }
    if (isAdmin && !form.organization_id && !editingPolicy) {
      setMessage({ type: "error", text: "Organization is required." });
      return;
    }
    const invalidUserStep = form.steps.find(
      (s) => s.role === "user" && !s.user_id
    );
    if (invalidUserStep) {
      setMessage({
        type: "error",
        text: "Select a specific user for each step with role “Specific user”.",
      });
      return;
    }
    const payload = {
      entity_code: form.entity_code,
      action: form.action,
      name: form.name.trim(),
      enabled: form.enabled,
      steps: form.steps.map((s, i) => buildPolicyStepPayload(s, i + 1)),
    };
    if (isAdmin && form.organization_id) payload.organization_id = Number(form.organization_id);
    if (showMinAmount && form.min_amount !== "") payload.min_amount = String(form.min_amount);

    const result = editingPolicy
      ? await savePolicy(payload, editingPolicy.id)
      : await savePolicy(payload);

    if (result.success) {
      setMessage({ type: "success", text: editingPolicy ? "Policy updated." : "Policy saved." });
      setDialogOpen(false);
      refresh();
    } else {
      setMessage({ type: "error", text: result.message });
    }
  };

  const handleDeactivate = async (policy) => {
    if (!window.confirm(`Deactivate policy "${policy.name}"?`)) return;
    const result = await deactivatePolicy(policy.id);
    if (result.success) {
      setMessage({ type: "success", text: "Policy deactivated." });
      refresh();
    } else {
      setMessage({ type: "error", text: result.message });
    }
  };

  const toggleEnabled = async (policy) => {
    const result = await savePolicy({ enabled: !policy.enabled }, policy.id);
    if (result.success) refresh();
    else setMessage({ type: "error", text: result.message });
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type || "info"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}
      {policyActionError && <Alert severity="error" sx={{ mb: 2 }}>{policyActionError}</Alert>}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Configure maker-checker rules per organization for the various approval entities.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add policy
        </Button>
      </Box>

      {isLoadingPolicies ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : policiesError ? (
        <Alert severity="error">Failed to load policies.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Min amount</TableCell>
                <TableCell>Steps</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(policies ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No policies configured.</TableCell>
                </TableRow>
              ) : (
                policies.map((policy) => (
                  <TableRow key={policy.id} hover>
                    <TableCell>{policy.name}</TableCell>
                    <TableCell>{policy.entity_code}</TableCell>
                    <TableCell>{policy.action}</TableCell>
                    <TableCell>{policy.min_amount ?? "—"}</TableCell>
                    <TableCell>{(policy.steps ?? []).length}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={policy.enabled ? "Yes" : "No"}
                        color={policy.enabled ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(policy)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDeactivate(policy)}><DeleteIcon fontSize="small" /></IconButton>
                      <Button size="small" onClick={() => toggleEnabled(policy)}>
                        {policy.enabled ? "Disable" : "Enable"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPolicy ? "Edit policy" : "New approval policy"}</DialogTitle>
        <DialogContent dividers>
          {isAdmin && (
            <FormControl fullWidth sx={{ mb: 2 }} size="small">
              <InputLabel>Organization</InputLabel>
              <Select
                value={form.organization_id}
                label="Organization"
                onChange={(e) => setForm((p) => ({ ...p, organization_id: e.target.value }))}
              >
                {(organisations ?? []).map((org) => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Entity</InputLabel>
            <Select
              value={form.entity_code}
              label="Entity"
              onChange={(e) => {
                const code = e.target.value;
                const ent = approvalEntities.find((x) => x.code === code);
                setForm((p) => ({
                  ...p,
                  entity_code: code,
                  action: ent?.actions?.[0] ?? "CREATE",
                  min_amount: "",
                }));
              }}
            >
              {approvalEntities.length === 0 ? (
                <MenuItem disabled value="">No approval entities available</MenuItem>
              ) : (
                approvalEntities.map((ent) => (
                  <MenuItem key={ent.code} value={ent.code}>{ent.label}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {selectedEntity?.description && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {selectedEntity.description}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Action</InputLabel>
            <Select
              value={form.action}
              label="Action"
              onChange={(e) => setForm((p) => ({ ...p, action: e.target.value }))}
            >
              {(selectedEntity?.actions ?? ["CREATE"]).map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Policy name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            sx={{ mb: 2 }}
            size="small"
          />
          {showMinAmount && (
            <TextField
              fullWidth
              label="Minimum amount (optional)"
              type="number"
              value={form.min_amount}
              onChange={(e) => setForm((p) => ({ ...p, min_amount: e.target.value }))}
              helperText="Leave empty to always require approval"
              sx={{ mb: 2 }}
              size="small"
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
              />
            }
            label="Enabled"
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>Approval steps</Typography>
          {form.steps.map((step, index) => (
            <Box key={index} sx={{ mb: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ minWidth: 24 }}>{index + 1}.</Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={step.role}
                    label="Role"
                    onChange={(e) => handleStepChange(index, "role", e.target.value)}
                  >
                    {POLICY_ROLES.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label="Required"
                  value={step.required_approvals}
                  onChange={(e) => handleStepChange(index, "required_approvals", e.target.value)}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1 }}
                />
                {step.role !== "user" && (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={step.scope}
                      label="Scope"
                      onChange={(e) => handleStepChange(index, "scope", e.target.value)}
                    >
                      {POLICY_SCOPES.map((s) => (
                        <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {form.steps.length > 1 && (
                  <IconButton size="small" onClick={() => removeStep(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {step.role === "user" && (
                <FormControl fullWidth size="small">
                  <InputLabel>Specific user</InputLabel>
                  <Select
                    value={step.user_id || ""}
                    label="Specific user"
                    onChange={(e) => handleStepChange(index, "user_id", e.target.value)}
                    disabled={isLoadingApprovers}
                  >
                    {isLoadingApprovers ? (
                      <MenuItem disabled value="">
                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading users…
                      </MenuItem>
                    ) : eligibleApprovers.length === 0 ? (
                      <MenuItem disabled value="">No eligible users — select organization first</MenuItem>
                    ) : (
                      eligibleApprovers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.display_name || `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()}
                          {user.role ? ` (${user.role})` : ""}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addStep}>Add step</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isSubmittingPolicy}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmittingPolicy}>
            {isSubmittingPolicy ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalPoliciesTab;
